import axios, {AxiosError, AxiosInstance, AxiosResponse} from 'axios'
import {APIError, BaseError, ErrorSeverity} from "@/model/error";
import config from "@/config";
import {normalizeImageFile} from "@/composables/imageNormalizer";
import {Image} from "@/model/image";
import {galleryAuth} from "@/stores/auth";
import {ref} from "vue";

const apiPathVersion = 'api/v1'

export const authFailure = ref<boolean>(false)

export class APIResponse<T> {
    errors: string[]
    data: T
    constructor(errors: string[], data: T) {
        this.errors = errors
        this.data = data
    }
}

const convertAxiosResponse = <T>(res: AxiosResponse): APIResponse<T> => {
    const errs = res.data.errors !== undefined ? res.data.errors : []
    return new APIResponse<T>(errs, res.data.data)
}

export async function performAPICall<T>(call: Promise<AxiosResponse>): Promise<APIResponse<T>> {
    return new Promise<APIResponse<T>>((resolve, reject) => {
        call
            .then(response => {
                if (response.headers["force-reload"] === "true") {
                    window.location.reload()
                    return
                }
                if (response.status >= 200 && response.status < 400) {
                    resolve(convertAxiosResponse(response))
                } else {
                    const err = new APIError("API communication error.", ErrorSeverity.ERROR)
                    reject(err)
                }})
            .catch((err: AxiosError) => {
                if (err.response?.status === 403) {
                    authFailure.value = true
                }
                const error = new APIError("API communication error.", ErrorSeverity.ERROR, err.response?.status, err)
                reject(error)
            })
    })
}

export class BoothyAPI {
    private client: AxiosInstance
    constructor(apiBaseUrl: string) {
        this.client = axios.create({baseURL: apiBaseUrl})
    }

    public async uploadImages(files: File[]): Promise<void> {
        const normalized:Blob[] = []
        for (let i = 0; i < files.length; i++) {
            const blob = await normalizeImageFile(files[i])
            normalized.push(blob)
        }
        const data = new FormData()
        normalized.forEach(img => {
            data.append("images[]", img)
        })
        return performAPICall<void>(this.client.post("/images", data, {headers: {"Content-Type": "multipart/form-data", 'boothy-auth': galleryAuth.value ? galleryAuth.value : ''}}))
            .then(() => {return})
    }

    public async getImages(opts?: {before?: number, since?: number}): Promise<Image[]>{
        let q = ""
        if (opts) {
            q = "?"
            if (opts.before) {
                q+=`before=${opts.before}`
            }
            if (opts.since) {
                q+=`since=${opts.since}`
            }
        }
        return performAPICall<Image[]>(
            this.client.get(`/images${q}`, {headers: {'boothy-auth': galleryAuth.value ? galleryAuth.value : ''}}))
                .then((data:APIResponse<Image[]>) => {
                return data.data as Image[]
            })
    }

    public async deleteImages(imgs: number[]): Promise<void>{
        return performAPICall<void>(this.client.post("/images/deletion", imgs, {headers: {'boothy-auth': galleryAuth.value ? galleryAuth.value : ''}})).then(() => {return})
    }
}

export function useAPI(apiHost: string): BoothyAPI {
    return new BoothyAPI(`${apiHost}/${apiPathVersion}`)
}

export function useDefaultAPI(): BoothyAPI {
    return useAPI(config.httpHost())
}
